import 'twin.macro';
import React, { useState, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import useCreateEleve from '@teetsh/app/src/queries/eleves/useCreateEleve';
import { useNavigate } from 'react-router-dom';
import { Menu } from '@teetsh/app/src/components/Menu';
import { ArrowsRightLeftIcon as SwitchHorizontal } from '@heroicons/react/20/solid';
import { EllipsisHorizontalIcon as EllipsisHorizontal } from '@heroicons/react/24/outline';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { DocumentPlusIcon as DocumentAdd } from '@heroicons/react/24/outline';
import { TrashIcon as Trash } from '@heroicons/react/24/outline';
import { DeleteElevesWarningModal } from './DeleteElevesWarningModal';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { SchoolSelectorModal } from '@teetsh/app/src/components/EntitiesSelectorModals/SchoolSelectorModal';
import useEleves from '@teetsh/app/src/queries/eleves/useEleves';
import { QuestionMarkCircleIcon as QuestionMarkCircle } from '@heroicons/react/24/outline';
import { TagIcon } from '@heroicons/react/24/outline';
import ButtonExternal from '@teetsh/app/src/components/Buttons/ExternalLink';
import { tutoUrls } from 'containers/tutoUrls';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  useGenerateEtiquettesLink,
  useInvalidateEtiquettesLink,
} from '@teetsh/app/src/queries/eleves/views/useGenerateEtiquettesLink';

interface Props {
  loading: boolean;
  mobile?: boolean;
}

function MenuItems({
  loading,
  viewId,
  setIsOpen,
  navigate,
  schoolyear,
  school,
  setIsWarningModalOpen,
}: {
  loading: boolean;
  viewId: string | undefined;
  setIsOpen: (isOpen: boolean) => void;
  navigate: any;
  schoolyear: any;
  school: any;
  setIsWarningModalOpen: (isOpen: boolean) => void;
}) {
  const invalidateEtiquettesLink = useInvalidateEtiquettesLink();
  const { t } = useTranslation(['eleve', 'common']);
  const { data: etiquettesData } = useGenerateEtiquettesLink(viewId);
  const etiquettesUrl = `${import.meta.env.VITE_WEBSITE_URL}${
    etiquettesData?.path
  }`;

  useEffect(
    () => () => {
      if (viewId) {
        invalidateEtiquettesLink(viewId);
      }
    },
    [viewId, invalidateEtiquettesLink],
  );

  return (
    <>
      <Menu.Item
        disabled={loading}
        onClick={() => setIsOpen(true)}
        text={t('eleve:actions.copyElevesOnAnotherSchool')}
        tw="whitespace-nowrap"
        icon={SwitchHorizontal}
      />
      <Menu.Item
        disabled={loading}
        onClick={() => navigate('preview')}
        text={t('common:buttons.pdfPreview')}
        tw="whitespace-nowrap"
        icon={PrinterIcon}
      />
      <Menu.Item disabled={loading} tw="whitespace-nowrap">
        <ButtonExternal
          target="_blank"
          href={etiquettesUrl}
          icon={TagIcon}
          large
          minimal
          fill
          alignText="left"
        >
          {t('eleve:actions.createLabels')}
        </ButtonExternal>
      </Menu.Item>
      <Menu.Item
        disabled={loading}
        onClick={() =>
          navigate(
            `/annee-scolaire/${schoolyear.id}/ecole/${school.id}/eleves/import/file`,
          )
        }
        text={t('eleve:actions.importElevesFromFile')}
        tw="whitespace-nowrap"
        icon={DocumentAdd}
      />
      <Menu.Item disabled={loading} tw="whitespace-nowrap">
        <ButtonExternal
          target="_blank"
          href={tutoUrls.eleveVideoTutorialUrl}
          icon={QuestionMarkCircle}
          large
          minimal
          fill
          alignText="left"
        >
          {t('eleve:openVideoTutorial')}
        </ButtonExternal>
      </Menu.Item>
      <Menu.Item
        disabled={loading}
        onClick={() => setIsWarningModalOpen(true)}
        text={t('eleve:actions.deleteAllEleves')}
        intent="danger"
        tw="whitespace-nowrap"
        icon={Trash}
      />
    </>
  );
}

export function Settings(props: Props) {
  const { data, isLoading, isFetched } = useEleves();
  const { t } = useTranslation(['eleve', 'common']);
  const { loading } = props;
  const { school, schoolyear } = useSchoolSchoolyear();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);
  const createEleve = useCreateEleve();
  const navigate = useNavigate();
  const { viewId } = useParams<{ viewId: string }>();

  if (!data || isLoading || !isFetched) {
    return null;
  }
  return (
    <>
      <DeleteElevesWarningModal
        isOpen={isWarningModalOpen}
        eleves={data}
        onClose={() => setIsWarningModalOpen(false)}
      />
      <Menu large portal>
        <Menu.Button
          small
          icon={EllipsisHorizontal}
          minimal
          outlined
          tw="px-2"
        />
        <Menu.Items size="auto">
          <MenuItems
            loading={loading}
            viewId={viewId}
            setIsOpen={setIsOpen}
            navigate={navigate}
            schoolyear={schoolyear}
            school={school}
            setIsWarningModalOpen={setIsWarningModalOpen}
          />
        </Menu.Items>
      </Menu>
      <SchoolSelectorModal
        isOpen={isOpen}
        title={t('eleve:chooseSchoolToCopyEleveTo')}
        confirmLabel={t('common:buttons.copy')}
        onClose={() => setIsOpen(false)}
        onConfirm={({ schoolyear: newSchoolyear, school: newSchool }) =>
          createEleve({
            eleves: data.map(eleve => ({
              ...eleve,
              school_id: newSchool.id,
              id: uuid(),
            })),
            schoolyearId: newSchoolyear.id,
            schoolId: newSchool.id,
          }).then(() => {
            navigate(
              `/annee-scolaire/${newSchoolyear.id}/ecole/${newSchool.id}/eleves`,
            );
          })
        }
      />
    </>
  );
}
